// client/src/components/common/ImageZoomModal.js
import React, { useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react';

const ImageZoomModal = ({ image, isOpen, onClose }) => {
    const [scale, setScale] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            setScale(1);
            setRotation(0);
            setPosition({ x: 0, y: 0 });
        }
    }, [image, isOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27) onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
    const handleResetZoom = () => setScale(1);
    const handleRotate = () => setRotation(prev => (prev + 90) % 360);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = image.url;
        link.download = image.nomeOriginal || 'imagem';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = useCallback((e) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart, scale]);

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove]);

    if (!isOpen || !image) return null;

    return (
        <div className="image-zoom-modal-overlay" onClick={onClose}>
            <div className="image-zoom-modal-content" onClick={e => e.stopPropagation()}>
                <div className="image-zoom-header">
                    <div className="image-zoom-info">
                        <span className="image-name">
                            {image.nomeOriginal || 'Imagem'}
                        </span>
                        <span className="image-size">
                            {scale === 1 ? 'Tamanho original' : `${Math.round(scale * 100)}%`}
                        </span>
                    </div>
                    
                    <div className="image-zoom-controls">
                        <button 
                            onClick={handleZoomOut}
                            disabled={scale <= 0.5}
                            className="zoom-control-btn"
                            title="Zoom Out"
                        >
                            <ZoomOut size={18} />
                        </button>
                        
                        <button 
                            onClick={handleResetZoom}
                            className="zoom-control-btn"
                            title="Reset Zoom"
                        >
                            {Math.round(scale * 100)}%
                        </button>
                        
                        <button 
                            onClick={handleZoomIn}
                            disabled={scale >= 3}
                            className="zoom-control-btn"
                            title="Zoom In"
                        >
                            <ZoomIn size={18} />
                        </button>

                        <button 
                            onClick={handleRotate}
                            className="zoom-control-btn"
                            title="Rotacionar"
                        >
                            <RotateCw size={18} />
                        </button>

                        <button 
                            onClick={handleDownload}
                            className="zoom-control-btn"
                            title="Download"
                        >
                            <Download size={18} />
                        </button>

                        <button 
                            onClick={onClose}
                            className="zoom-control-btn close-btn"
                            title="Fechar"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="image-zoom-body">
                    <div 
                        className="image-container"
                        style={{
                            transform: `scale(${scale}) rotate(${rotation}deg)`,
                            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            left: `${position.x}px`,
                            top: `${position.y}px`,
                            position: scale > 1 ? 'relative' : 'static'
                        }}
                        onMouseDown={handleMouseDown}
                    >
                        <img 
                            src={image.url} 
                            alt={image.nomeOriginal || 'Imagem ampliada'}
                            className="zoomed-image"
                            onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EwYWIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIwLjNlbSI+RXJybyBhbyBjYXJyZWdhciBpbWFnZW08L3RleHQ+PC9zdmc+';
                            }}
                        />
                    </div>
                </div>

                {scale > 1 && (
                    <div className="image-zoom-footer">
                        <span className="zoom-hint">
                            🖱️ Arraste para mover • Esc para fechar
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageZoomModal;